'use client';
import React, { useState } from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import InputField from '@/UIlibrary/InputField/InputField';
import logger from '@/logger';
import { useAuth } from '../CustomerSupport/useAuth';
import { useSearchParams } from 'react-router-dom';
import Logo from '@/stories/Components/Logo/Logo';

const SupportPage: React.FC = () => {
	const t = useTranslations('Support');
	const { loading: authLoading, error: authError, signIn, session, isSessionExpired } = useAuth();
	const [searchParams] = useSearchParams();

	const [formData, setFormData] = useState({
		email: '',
		password: ''
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
		if (error) setError(null);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (loading) return;

		if (!formData.email.trim() || !formData.password.trim()) {
			setError(t('invalidCredentials'));
			return;
		}

		setLoading(true);
		setError(null);

		try {
			await signIn(formData.email, formData.password, searchParams);
		} catch (err) {
			logger.error('Login error:', err);
			setError(t(toTranslationKey(authError || 'invalidCredentials')));
		} finally {
			setLoading(false);
		}
	};

	// Show loading state
	if (authLoading) {
		return (
			<div className="p-8">
				<Typography size="sm">{t('loading')}</Typography>
			</div>
		);
	}

	// Return early if valid session exists
	if (session && !isSessionExpired(session)) return null;

	// Main render
	return (
		<div className="p-8 flex flex-col items-center justify-center h-screen bg-gray-50">
			<div className="flex flex-col items-center justify-center border border-gray-300 rounded-xl p-12 shadow-md bg-white">
				<Typography size="sm" weight="semibold" type="display">
					{t('Title')}
				</Typography>

				<form
					onSubmit={handleSubmit}
					className="flex flex-col gap-6 mt-4 w-full max-w-[500px] justify-center items-center"
				>
					<InputField
						label={t('Email')}
						type="email"
						name="email"
						value={formData.email}
						onChange={handleChange}
						required={true}
						error={!!error}
						hint={error || ''}
						disabled={loading}
						loading={loading}
						id="email-input"
						dataTestId="email-input"
					/>

					<InputField
						label={t('Password')}
						type="password"
						name="password"
						value={formData.password}
						onChange={handleChange}
						required={true}
						error={!!error}
						hint={error || ''}
						disabled={loading}
						loading={loading}
						id="password-input"
						dataTestId="password-input"
					/>

					<div className="w-full">
						<Button
							color="primary"
							label={t('CustomerSupportPortal')}
							size="full"
							type="submit"
							disabled={loading || !formData.email || !formData.password}
						/>
					</div>
					<div className="flex flex-row gap-2 items-end justify-end mt-4">
						<Typography size="sm" color="subtitle">
							Powered by
						</Typography>
						<Logo src="/iluk-logo-light.svg" width={50} height={25} />
					</div>
				</form>
			</div>
		</div>
	);
};

export default SupportPage;
